import { type SkillsAndTools_userProfile$key } from '#app/__generated__/relay/SkillsAndTools_userProfile.graphql.js';
import { InlineEditContainer } from '#app/components/InlineEditContainer/InlineEditContainer.js';
import { Text } from '#app/components/Primitives/Text.js';
import { StyledTagBox } from '#app/components/Tags/Tag.js';
import { TagListMenu } from '#app/components/Tags/TagListMenu/TagListMenu.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { InlineEditingEmptyPlaceholder } from '../InlineEditingEmptyPlaceholder.js';
import { EditIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { isTruthy } from '@contra/utilities/isTruthy';
import { lazy, Suspense, useState } from 'react';
import { styled } from 'styled-components';

const LazySkillsAndToolsInlineEditing = lazy(async () => {
  return {
    default: (await import('./SkillsAndToolsInlineEditing.js'))
      .SkillsAndToolsInlineEditing,
  };
});

const itemHeight = 32;

const TagListMenuContainer = styled.div`
  ${StyledTagBox} {
    height: ${itemHeight}px;
    border-radius: 20px;

    ${Text} {
      font-size: 14px;
    }
  }
`;

type SkillsAndToolsShape = {
  id: string;
  kind: 'skill' | 'tool';
  logoImage?: null | { uid: string };
  name: string;
};

export const SkillsAndTools = ({
  overriddenTags,
  userProfileRef,
}: {
  readonly overriddenTags?: readonly SkillsAndToolsShape[];
  readonly userProfileRef: SkillsAndTools_userProfile$key;
}) => {
  const data = useFragment(
    graphql`
      fragment SkillsAndTools_userProfile on UserProfile {
        tags(
          tagTypes: [ROLE_TAG, TOOL_TAG]
          tagContexts: [WORK_PREFERENCE, PROFILE]
        ) {
          __typename
          id
          name
          ... on UserProfileRoleTag {
            role {
              id
              name
            }
          }
          ... on UserProfileToolTag {
            tool {
              id
              name
              logoImage {
                uid
              }
            }
          }
        }

        visitorCanEdit
      }
    `,
    userProfileRef,
  );

  const { tags, visitorCanEdit } = data;

  const skills = tags
    .map((tag) => {
      if (tag.__typename !== 'UserProfileRoleTag' || !tag.role) {
        return null;
      }

      return {
        id: tag.role.id,
        kind: 'skill' as const,
        name: tag.role.name,
      };
    })
    .filter(isTruthy);

  const tools = tags
    .map((tag) => {
      if (tag.__typename !== 'UserProfileToolTag' || !tag.tool) {
        return null;
      }

      return {
        id: tag.tool.id,
        kind: 'tool' as const,
        logoImage: tag.tool.logoImage?.uid
          ? { uid: tag.tool.logoImage?.uid }
          : null,
        name: tag.tool.name,
      };
    })
    .filter(isTruthy);

  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const currentUser = useCurrentUser();

  const skillAndToolTags = (
    <TagListMenuContainer>
      <TagListMenu<SkillsAndToolsShape>
        itemHeight={itemHeight}
        items={overriddenTags ?? [...skills, ...tools]}
        maxRows={2}
        readOnly={visitorCanEdit}
        transformItemToTag={(item) => {
          /**
           * Match Hire page slugs in Contra Landing Pages
           * https://github.com/contra/contra-landing-pages/blob/236121c55f0fb38f1562045423fa4fbbb3474a93/src/pages/hire/%5Bslug%5D/index.tsx#L140
           */
          const pluralizedRoute = (
            item.kind === 'skill' ? `${item.name}s` : `${item.name}-experts`
          )
            .toLowerCase()
            .replaceAll(' ', '-');

          const href = currentUser
            ? getRoute('/discover/independents', {
                search: {
                  query: item.name,
                },
              })
            : `/hire/${pluralizedRoute}`;

          return {
            href: visitorCanEdit ? undefined : href,
            label: item.name,
            value: item.id,
            variant: visitorCanEdit ? 'default' : 'condensed-link',
          };
        }}
        variant={visitorCanEdit ? 'default' : 'condensed-link'}
      />
    </TagListMenuContainer>
  );

  const visitorCanEditDefaultState = (
    <>
      {tags.length > 0 ? (
        <InlineEditContainer
          label="Edit your skills and tools"
          onClick={() => setIsInlineEditing(true)}
          visitorCanEdit={visitorCanEdit}
        >
          {skillAndToolTags}
          <EditIcon size={16} />
        </InlineEditContainer>
      ) : null}
      {tools.length === 0 ? (
        <InlineEditingEmptyPlaceholder
          label={tags.length > 0 ? 'Add more tags' : 'Add tags'}
          onClick={() => {
            setIsInlineEditing(true);
          }}
        />
      ) : null}
    </>
  );

  if (visitorCanEdit) {
    if (isInlineEditing) {
      return (
        <Suspense fallback={visitorCanEditDefaultState}>
          <LazySkillsAndToolsInlineEditing
            initialTools={tools}
            isInlineEditing={isInlineEditing}
            setIsInlineEditing={setIsInlineEditing}
            skills={skills}
          />
        </Suspense>
      );
    }

    return visitorCanEditDefaultState;
  }

  if (tags.length > 0) {
    return skillAndToolTags;
  }

  return null;
};
